// File: frontend/src/App.jsx
// Main App component for WA Contact Center CRM

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import Layout from './components/Layout/Layout';
import AuthLayout from './components/Layout/AuthLayout';

// Pages
import LoginPage from './pages/Auth/LoginPage';
import GoogleCallbackPage from './pages/Auth/GoogleCallbackPage';
import DashboardPage from './pages/Dashboard/DashboardPage';
import LeadsPage from './pages/Leads/LeadsPage';
import ContactsPage from './pages/Contacts/ContactsPage';
import CompaniesPage from './pages/Companies/CompaniesPage';
import CompanyDetailPage from './pages/Companies/CompanyDetailPage';
import ContactDetailPage from './pages/Contacts/ContactDetailPage';
import NewContactPage from './pages/Contacts/NewContactPage';
import OpportunitiesPage from './pages/Opportunities/OpportunitiesPage';
import PipelinePage from './pages/Pipeline/PipelinePage';
import ActivitiesPage from './pages/Activities/ActivitiesPage';
import ReportsPage from './pages/Reports/ReportsPage';
import AnalyticsPage from './pages/Analytics/AnalyticsPage';
import UsersPage from './pages/Users/UsersPage';
import SettingsPage from './pages/Settings/SettingsPage';
import LeadDetailPage from './pages/Leads/LeadDetailPage';
import OpportunityDetailPage from './pages/Pipeline/OpportunityDetailPage';
import MeetingsReviewPage from './pages/Meetings/MeetingsReviewPage';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <div className="App">
            <Routes>
              {/* Public routes */}
              <Route
                path="/login"
                element={
                  <AuthLayout>
                    <LoginPage />
                  </AuthLayout>
                }
              />

              <Route
                path="/auth/google/callback"
                element={<GoogleCallbackPage />}
              />

              {/* Protected routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DashboardPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/leads"
                element={
                  <ProtectedRoute requiredPermission={['leads', 'read']}>
                    <Layout>
                      <LeadsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/leads/:id"
                element={
                  <ProtectedRoute requiredPermission={['leads', 'read']}>
                    <Layout>
                      <LeadDetailPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/contacts"
                element={
                  <ProtectedRoute requiredPermission={['contacts', 'read']}>
                    <Layout>
                      <ContactsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/contacts/new"
                element={
                  <ProtectedRoute requiredPermission={['contacts', 'create']}>
                    <Layout>
                      <NewContactPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/contacts/:id"
                element={
                  <ProtectedRoute requiredPermission={['contacts', 'read']}>
                    <Layout>
                      <ContactDetailPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/companies"
                element={
                  <ProtectedRoute requiredPermission={['companies', 'read']}>
                    <Layout>
                      <CompaniesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/companies/:id"
                element={
                  <ProtectedRoute requiredPermission={['companies', 'read']}>
                    <Layout>
                      <CompanyDetailPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/opportunities"
                element={
                  <ProtectedRoute requiredPermission={['opportunities', 'read']}>
                    <Layout>
                      <OpportunitiesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/pipeline"
                element={
                  <ProtectedRoute requiredPermission={['opportunities', 'read']}>
                    <Layout>
                      <PipelinePage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/opportunities/:id"
                element={
                  <ProtectedRoute requiredPermission={['opportunities', 'read']}>
                    <Layout>
                      <OpportunityDetailPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/activities"
                element={
                  <ProtectedRoute requiredPermission={['activities', 'read']}>
                    <Layout>
                      <ActivitiesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/reports"
                element={
                  <ProtectedRoute requiredPermission={['reports', 'read']}>
                    <Layout>
                      <ReportsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute requiredPermission={['reports', 'read']}>
                    <Layout>
                      <AnalyticsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/users"
                element={
                  <ProtectedRoute requiredPermission={['users', 'read']}>
                    <Layout>
                      <UsersPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SettingsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/meetings/review"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <MeetingsReviewPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              
              {/* 404 fallback */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>

            {/* Global toast notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#32373c', // WA Corporate Gray
                  color: '#fff',
                  fontFamily: 'Roboto, sans-serif',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#10b981', // Keep green for success
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#e10600', // WA Red for errors
                    secondary: '#fff',
                  },
                },
                loading: {
                  iconTheme: {
                    primary: '#e10600', // WA Red for loading
                    secondary: '#fff',
                  },
                },
              }}
            />
          </div>
        </Router>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;